import {
  ArrowRight,
  Check,
  CheckCircle,
  List,
  Package,
  Quotes,
  Receipt,
  SealCheck,
  X,
} from "@phosphor-icons/react";
import { FormEvent, useMemo, useState } from "react";
import { finishOptions, giftOptions, teaLayers } from "./data/campaign";
import { formatVnd } from "./lib/format";

type FormErrors = Partial<Record<"name" | "phone" | "consent", string>>;

const base = import.meta.env.BASE_URL;

export function App() {
  const [giftId, setGiftId] = useState(giftOptions[1].id);
  const [finishId, setFinishId] = useState<(typeof finishOptions)[number]["id"]>("xanh-vu");
  const [message, setMessage] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [receipt, setReceipt] = useState<{ name: string; code: string } | null>(() => {
    const saved = localStorage.getItem("nam-diep-preorder");
    return saved ? JSON.parse(saved) : null;
  });

  const selectedGift = useMemo(
    () => giftOptions.find((gift) => gift.id === giftId) ?? giftOptions[1],
    [giftId],
  );
  const selectedFinish = finishOptions.find((finish) => finish.id === finishId)!;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const name = String(data.get("name") ?? "").trim();
    const phone = String(data.get("phone") ?? "").replace(/\s/g, "");
    const consent = data.get("consent") === "on";
    const nextErrors: FormErrors = {};

    if (name.length < 2) nextErrors.name = "Nhập tên người đặt, ít nhất 2 ký tự.";
    if (!/^(0|\+84)\d{9}$/.test(phone)) nextErrors.phone = "Nhập số điện thoại Việt Nam hợp lệ.";
    if (!consent) nextErrors.consent = "Bạn cần đồng ý để lưu yêu cầu trong trình duyệt này.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    const nextReceipt = { name, code: `ND26-${selectedGift.id === "ba-vung" ? "3T" : selectedGift.id === "hai-vung" ? "2T" : "BD"}-0719` };
    localStorage.setItem("nam-diep-preorder", JSON.stringify(nextReceipt));
    setReceipt(nextReceipt);
  }

  function resetDemo() {
    localStorage.removeItem("nam-diep-preorder");
    setReceipt(null);
    setDialogOpen(false);
    setGiftId("ba-vung");
    setFinishId("xanh-vu");
    setMessage("");
    setErrors({});
  }

  return (
    <div className="site-shell">
      <header className="topbar">
        <a className="brand" href="#top" aria-label="NAM DIỆP, về đầu trang">
          <span className="brand-mark" aria-hidden="true">ND</span>
          <span>NAM DIỆP</span>
        </a>
        <nav className="desktop-nav" aria-label="Điều hướng chính">
          <a href="#mua-tra">Tuyển chọn</a>
          <a href="#hop-qua">Chọn hộp</a>
          <a href="#minh-bach">Nguồn gốc</a>
          <button className="nav-cta" type="button" onClick={() => setDialogOpen(true)}>Đặt trước</button>
        </nav>
        <button className="menu-button" type="button" onClick={() => setMobileOpen((open) => !open)} aria-expanded={mobileOpen} aria-controls="mobile-nav" aria-label="Mở điều hướng">
          {mobileOpen ? <X size={24} /> : <List size={24} />}
        </button>
        {mobileOpen && (
          <nav id="mobile-nav" className="mobile-nav" aria-label="Điều hướng di động">
            <a href="#mua-tra" onClick={() => setMobileOpen(false)}>Tuyển chọn</a>
            <a href="#hop-qua" onClick={() => setMobileOpen(false)}>Chọn hộp</a>
            <a href="#minh-bach" onClick={() => setMobileOpen(false)}>Nguồn gốc</a>
            <button type="button" onClick={() => { setDialogOpen(true); setMobileOpen(false); }}>Đặt trước</button>
          </nav>
        )}
      </header>

      <main id="top">
        <section className="hero">
          <div className="hero-copy">
            <p className="eyebrow">Tuyển chọn Hè 2026</p>
            <h1>Ba vùng trà.<br />Một hộp quà rõ nguồn gốc.</h1>
            <p>Chọn hai hoặc ba mẻ trà cao vụ, hoàn thiện hộp và lời nhắn trước khi đặt trước.</p>
            <div className="hero-actions">
              <a className="button primary" href="#hop-qua">Chọn hộp quà <ArrowRight size={18} /></a>
              <a className="text-link" href="#mua-tra">Xem ba sắc trà</a>
            </div>
          </div>
          <figure className="hero-media">
            <img src={`${base}media/hero-gift.webp`} alt="Hộp quà NAM DIỆP mở nắp với ba lon trà bạc và thiệp trắng" width="1536" height="1024" fetchPriority="high" />
          </figure>
        </section>

        <section className="campaign-note" aria-label="Thông tin phát hành">
          <p>Mở đặt trước đến 18.08</p>
          <p>Giao dự kiến từ 28.08</p>
          <p>Toàn bộ giao dịch trong demo là mô phỏng</p>
        </section>

        <section className="story" id="mua-tra">
          <div className="story-media">
            <img src={`${base}media/origin-slope.webp`} alt="Hai người hái trà trên sườn núi phủ sương buổi sớm" width="1536" height="960" loading="lazy" />
          </div>
          <div className="story-copy">
            <h2>Ba vùng, ba kiểu chế biến.</h2>
            <p>Chúng tôi chọn ba mẻ trà có nhịp vị khác nhau, rồi đóng cùng một hộp để người nhận đi từ thanh đến đậm trong một buổi.</p>
            <dl className="source-facts">
              <div><dt>Vùng trà</dt><dd>Yên Bái, Sơn La, Hà Giang</dd></div>
              <div><dt>Ngày đóng gói</dt><dd>19.08.2026</dd></div>
              <div><dt>Mẻ minh họa</dt><dd>VT-CS26-04</dd></div>
            </dl>
          </div>
        </section>

        <section className="tea-ledger">
          <h2>Ba hồ sơ vị, xếp từ thanh đến đậm.</h2>
          <div className="tea-list">
            {teaLayers.map((tea, index) => (
              <article className="tea-row" key={tea.name}>
                <span className="tea-index">{String(index + 1).padStart(2, "0")}</span>
                <div><p className="tea-type">{tea.type}</p><h3>{tea.name}</h3></div>
                <p>{tea.taste}</p>
                <div><small>{tea.origin}</small><strong>{tea.brew}</strong></div>
              </article>
            ))}
          </div>
        </section>

        <section className="configurator" id="hop-qua">
          <div className="config-media">
            <img src={`${base}media/ritual-pour.webp`} alt="Trà màu hổ phách được rót từ bình thủy tinh cạnh lon trà bạc" width="1024" height="1280" loading="lazy" />
            <div className="price-tag" aria-live="polite">
              <span>Tạm tính</span>
              <strong>{formatVnd(selectedGift.price)}</strong>
            </div>
          </div>
          <div className="config-panel">
            <p className="eyebrow">Phối hộp của bạn</p>
            <h2>Chọn quy mô hộp.</h2>
            <fieldset>
              <legend>1. Cỡ hộp</legend>
              <div className="choice-stack">
                {giftOptions.map((gift) => (
                  <label className={`gift-choice ${gift.id === giftId ? "selected" : ""}`} key={gift.id}>
                    <input type="radio" name="gift" value={gift.id} checked={gift.id === giftId} onChange={() => setGiftId(gift.id)} />
                    <span className="check-space">{gift.id === giftId && <Check size={17} weight="bold" />}</span>
                    <span><strong>{gift.name}</strong><small>{gift.subtitle}</small></span>
                    <b>{formatVnd(gift.price)}</b>
                  </label>
                ))}
              </div>
            </fieldset>
            <fieldset>
              <legend>2. Hoàn thiện</legend>
              <div className="finish-grid">
                {finishOptions.map((finish) => (
                  <label className={finish.id === finishId ? "selected" : ""} key={finish.id}>
                    <input type="radio" name="finish" value={finish.id} checked={finish.id === finishId} onChange={() => setFinishId(finish.id)} />
                    <span className={`finish-swatch ${finish.id}`} aria-hidden="true" />
                    <strong>{finish.label}</strong>
                    <small>{finish.description}</small>
                  </label>
                ))}
              </div>
            </fieldset>
            <label className="message-field" htmlFor="gift-message">
              <span>3. Lời nhắn <small>{message.length}/90</small></span>
              <textarea id="gift-message" value={message} maxLength={90} onChange={(event) => setMessage(event.target.value)} placeholder="Viết một lời ngắn, chúng tôi in trên thiệp giấy sợi." />
            </label>
            <div className="selection-summary">
              <div><Package size={22} /><p><strong>{selectedGift.name}, {selectedFinish.label}</strong><span>{selectedGift.contents.join(" · ")}</span></p></div>
              <button className="button primary" type="button" onClick={() => setDialogOpen(true)}>Đặt trước hộp này</button>
            </div>
          </div>
        </section>

        <section className="proof" id="minh-bach">
          <div className="proof-heading">
            <SealCheck size={44} />
            <h2>Mở hộp là thấy rõ mẻ trà.</h2>
          </div>
          <div className="proof-grid">
            <article><span>01</span><h3>Mã mẻ</h3><p>Mỗi lon có vùng trà, ngày chế biến và ngày đóng gói minh họa.</p></article>
            <article><span>02</span><h3>Nếm thử trước</h3><p>Bản mô tả ghi rõ tầng hương và thông số pha, không dùng lời hứa chung chung.</p></article>
            <article><span>03</span><h3>Đổi lựa chọn</h3><p>Yêu cầu mô phỏng có thể chỉnh đến trước ngày 18.08 trong phiên demo.</p></article>
          </div>
        </section>

        <section className="quote-section">
          <Quotes size={34} />
          <blockquote>“Một hộp quà đủ chỉn chu, nhưng vẫn để người gửi tự nói lời của mình.”</blockquote>
          <p>Nhận xét minh họa của khách thử nghiệm nội bộ</p>
        </section>

        <section className="final-cta">
          <div>
            <h2>Đặt trước Tuyển chọn Hè 2026.</h2>
            <p>Không thanh toán thật. Yêu cầu chỉ được lưu cục bộ trên trình duyệt.</p>
          </div>
          <button className="button light" type="button" onClick={() => setDialogOpen(true)}>Đặt trước</button>
        </section>
      </main>

      <footer>
        <div className="brand"><span className="brand-mark" aria-hidden="true">ND</span><span>NAM DIỆP</span></div>
        <p>Thương hiệu, dữ liệu và giao dịch đều là nội dung minh họa cho demo tĩnh.</p>
        <a href="#top">Về đầu trang</a>
      </footer>

      {dialogOpen && (
        <div className="dialog-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) setDialogOpen(false); }}>
          <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="preorder-title">
            <button className="dialog-close" type="button" onClick={() => setDialogOpen(false)} aria-label="Đóng"><X size={22} /></button>
            {receipt ? (
              <div className="success-state" role="status">
                <CheckCircle size={54} weight="fill" />
                <p className="eyebrow">Đã ghi nhận mô phỏng</p>
                <h2 id="preorder-title">Cảm ơn, {receipt.name}.</h2>
                <p>Mã yêu cầu <strong>{receipt.code}</strong> đã được lưu trên trình duyệt này. Không có dữ liệu nào được gửi đi.</p>
                <div className="receipt-box">
                  <Receipt size={24} />
                  <div><span>{selectedGift.name}, {selectedFinish.label}</span><strong>{formatVnd(selectedGift.price)}</strong></div>
                </div>
                <button className="button primary" type="button" onClick={resetDemo}>Đặt lại demo</button>
              </div>
            ) : (
              <form onSubmit={submit} noValidate>
                <p className="eyebrow">Yêu cầu mô phỏng</p>
                <h2 id="preorder-title">Giữ hộp {selectedGift.name}</h2>
                <p className="dialog-intro">{selectedGift.note}</p>
                <label htmlFor="name">Tên người đặt</label>
                <input id="name" name="name" autoComplete="name" aria-invalid={Boolean(errors.name)} aria-describedby={errors.name ? "name-error" : undefined} />
                {errors.name && <span className="field-error" id="name-error">{errors.name}</span>}
                <label htmlFor="phone">Số điện thoại</label>
                <input id="phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="0912 345 678" aria-invalid={Boolean(errors.phone)} aria-describedby={errors.phone ? "phone-error" : "phone-help"} />
                <span className="field-help" id="phone-help">Chỉ dùng để minh họa validation, không gửi đi.</span>
                {errors.phone && <span className="field-error" id="phone-error">{errors.phone}</span>}
                <label className="consent">
                  <input type="checkbox" name="consent" />
                  <span>Tôi hiểu đây là demo và đồng ý lưu yêu cầu trong localStorage.</span>
                </label>
                {errors.consent && <span className="field-error">{errors.consent}</span>}
                <div className="dialog-total"><span>Tạm tính</span><strong>{formatVnd(selectedGift.price)}</strong></div>
                <button className="button primary full" type="submit">Xác nhận mô phỏng</button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
